import React, { FC, useState } from 'react'
import { I18n, Cache } from 'aws-amplify'
import { makeStyles, Theme, Grid, Link, Typography } from '@material-ui/core'

import { Input, PillButton } from 'components'
import { AuthFlowSteps } from 'types'
import { tokenName, validateEmail } from 'helpers'

interface SignUpProps {
  setUserEmail: (email: string) => void
  setAuthState: (state: AuthFlowSteps) => void
}
interface ISignUpErrors {
  emailError: string
}

export const SignUp: FC<SignUpProps> = ({ setAuthState, setUserEmail }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')
  const [emailError, setEmailError] = useState<string>('')

  const validateSignUp = (): ISignUpErrors => {
    let emailError

    if (!email) {
      emailError = I18n.get('requiredField')
    }

    if (email.length > 0 && !validateEmail(email)) {
      emailError = I18n.get('invalidEmail')
    }

    return {
      emailError
    }
  }

  const signUp = async () => {
    const { emailError } = validateSignUp()
    if (!!emailError) {
      setEmailError(emailError)
    } else {
      setLoading(true)
      try {
        setUserEmail(email)
        Cache.setItem(tokenName, { email })
        setAuthState(AuthFlowSteps.Register)
      } catch (error) {
        if (error.code === 'UsernameExistsException') {
          setEmailError(I18n.get('emailAlreadyRegistered'))
        }
        console.log(error)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      signUp()
    }
  }

  return (
    <Grid container direction='column' justify='center' spacing={4}>
      <Grid item>
        <Typography variant='h4' component='h2' dangerouslySetInnerHTML={{ __html: I18n.get('joinUs') }} />
      </Grid>
      <Grid item container alignItems='center'>
        <Typography paragraph display='inline'>
          <span>{I18n.get('alreadyHaveAnAccount')}</span>
          <Link component='button' className={classes.inlineButton} onClick={() => setAuthState(AuthFlowSteps.SignIn)}>
            {I18n.get('signIn')}
          </Link>
        </Typography>
      </Grid>
      <Grid item container spacing={4} justify='center'>
        <Grid item xs={12}>
          <Input
            required
            id='email'
            type='email'
            error={!!emailError}
            helperText={emailError}
            onFocus={() => setEmailError('')}
            label={I18n.get('email')}
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12}>
        <Typography variant='body2' paragraph classes={{ root: classes.emailNote }}>
          {I18n.get('registerToSecureYourSpot')}
        </Typography>
        <Typography variant='body2' paragraph classes={{ root: classes.emailNote }}>
          {I18n.get('emailWillBecomeUsername')}
        </Typography>
      </Grid>
      <Grid item container xs={12}>
        <PillButton loading={loading} type='submit' onClick={() => signUp()} className={classes.button} solid>
          {I18n.get('continue')}
        </PillButton>
      </Grid>
    </Grid>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    width: 165,
    marginTop: '0 !important'
  },
  input: {
    color: '#000',
    borderRadius: 0,
    '& .MuiInputBase-root': {
      backgroundColor: '#fff'
    },
    '& fieldset': {
      borderRadius: 0,
      borderColor: '#dadada',
      borderBottomColor: '#000'
    }
  },
  inlineButton: {
    margin: '0 .5rem',
    textDecoration: 'underline',
    verticalAlign: 'baseline'
  },
  emailNote: {
    color: '#979797',
    fontSize: '14px'
  }
}))
